import React, { useEffect, useMemo, useState } from "react";

import DateTextBox from "react-lib/apps/common/DateTextBox";
import formatComma from "react-lib/utils/formatComma";

import { AlertV4, ButtonV4, ChipV4, FieldV4, ModalV4, PaginationV4, SelectV4 } from "../common/ui";
import {
  CHANNEL_OPTIONS,
  ORDER_ACTION,
  ORDER_LIMIT,
  ORDER_SEQUENCE,
  STATUS_OPTIONS,
  cleanFilter,
  orderRowsFromApi,
  sumAmount,
  type OrderFilter,
  type OrderRow,
} from "./purchaseModelV4";

import "./_hisv4-purchase.scss";

type OrderSummaryState = Partial<{
  divisionId: number;
  filter: OrderFilter;
  items: unknown[];
  total: number;
  activePage: number;
  loading: boolean;
  error: string | null;
}>;

export type CardPurchaseOrdersV4Props = {
  runSequence: (params: Record<string, unknown>) => void;
  divisionId?: number;
  orderSummary?: OrderSummaryState | null;
  tabs?: React.ReactNode;
};

const EMPTY_FILTER: OrderFilter = {};

/** รายการสั่งซื้อของหน่วย (ใบแจ้งหนี้ที่มีแพ็กเกจ) */
const CardPurchaseOrdersV4 = (props: CardPurchaseOrdersV4Props) => {
  const [draft, setDraft] = useState<OrderFilter>(EMPTY_FILTER);
  const [applied, setApplied] = useState<OrderFilter>(EMPTY_FILTER);
  const [openRow, setOpenRow] = useState<OrderRow | null>(null);

  const divisionId = Number(props.divisionId);
  const hasDivision = Number.isInteger(divisionId) && divisionId > 0;
  const summary = props.orderSummary?.divisionId === divisionId ? props.orderSummary : undefined;

  const total = summary?.total ?? 0;
  const page = summary?.activePage ?? 1;
  const pageCount = Math.max(1, Math.ceil(total / ORDER_LIMIT));
  const loading = !!summary?.loading;

  const rows = useMemo(() => orderRowsFromApi(summary?.items), [summary?.items]);

  useEffect(() => {
    setDraft(EMPTY_FILTER);
    setApplied(EMPTY_FILTER);

    handleLoad(EMPTY_FILTER);
  }, [divisionId]);

  const handleLoad = (filter: OrderFilter, activePage = 1) => {
    if (!hasDivision) {
      return;
    }

    setOpenRow(null);

    props.runSequence({
      sequence: ORDER_SEQUENCE,
      action: ORDER_ACTION,
      divisionId,
      filter: cleanFilter(filter),
      activePage,
    });
  };

  const handleSearch = () => {
    setApplied(draft);

    handleLoad(draft);
  };

  const handleReset = () => {
    setDraft(EMPTY_FILTER);
    setApplied(EMPTY_FILTER);

    handleLoad(EMPTY_FILTER);
  };

  const handleChangeFilter = (key: keyof OrderFilter, value: string) => {
    setDraft({ ...draft, [key]: value });
  };

  return (
    <div className="hv4-po">
      {props.tabs}

      <div className="hv4-po-head">
        <h1 className="hv4-h1">รายการสั่งซื้อ</h1>
        <ButtonV4
          variant="ghost"
          disabled={!hasDivision || loading}
          onClick={() => handleLoad(applied, page)}
        >
          ↻ รีเฟรช
        </ButtonV4>
      </div>

      <div className="hv4-po-filter">
        <FieldV4 label="เลขที่ใบแจ้งหนี้ / ชื่อลูกค้า" htmlFor="hv4-po-q">
          <input
            id="hv4-po-q"
            className="hv4-input"
            value={draft.search ?? ""}
            onChange={(e) => handleChangeFilter("search", e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </FieldV4>
        <FieldV4 label="ช่องทางชำระเงิน" htmlFor="hv4-po-channel">
          <SelectV4
            id="hv4-po-channel"
            options={CHANNEL_OPTIONS}
            placeholder="ทั้งหมด"
            value={draft.payment_channel ?? ""}
            onChange={(value) => handleChangeFilter("payment_channel", value)}
          />
        </FieldV4>
        <FieldV4 label="สถานะชำระเงิน" htmlFor="hv4-po-status">
          <SelectV4
            id="hv4-po-status"
            options={STATUS_OPTIONS}
            placeholder="ทั้งหมด"
            value={draft.status ?? ""}
            onChange={(value) => handleChangeFilter("status", value)}
          />
        </FieldV4>
        <FieldV4 label="วันที่สร้างรายการ ตั้งแต่" htmlFor="hv4-po-from">
          <div className="hv4-date">
            <DateTextBox
              id="hv4-po-from"
              value={draft.from_date ?? ""}
              onChange={(date: string) => handleChangeFilter("from_date", date)}
              placeholder="DD/MM/YYYY (พ.ศ.)"
              useAdDate={false}
              autoAdBe={true}
              inputFluid={true}
              inputStyle={{ width: "100%" }}
            />
          </div>
        </FieldV4>
        <FieldV4 label="ถึงวันที่" htmlFor="hv4-po-to">
          <div className="hv4-date">
            <DateTextBox
              id="hv4-po-to"
              value={draft.to_date ?? ""}
              onChange={(date: string) => handleChangeFilter("to_date", date)}
              placeholder="DD/MM/YYYY (พ.ศ.)"
              useAdDate={false}
              autoAdBe={true}
              inputFluid={true}
              inputStyle={{ width: "100%" }}
            />
          </div>
        </FieldV4>
        <div className="hv4-po-filter-actions">
          <ButtonV4 variant="primary" disabled={!hasDivision || loading} onClick={handleSearch}>
            ค้นหา
          </ButtonV4>
          <ButtonV4
            className="hv4-po-btn-soft"
            disabled={!hasDivision || loading}
            onClick={handleReset}
          >
            ล้างตัวกรอง
          </ButtonV4>
        </div>
      </div>

      {!hasDivision && <AlertV4 tone="info">กรุณาเลือกสาขาเพื่อดูรายการสั่งซื้อ</AlertV4>}
      {summary?.error && (
        <AlertV4
          tone="danger"
          actions={
            <ButtonV4 size="sm" variant="ghost" onClick={() => handleLoad(applied, page)}>
              ลองใหม่
            </ButtonV4>
          }
        >
          ไม่สามารถโหลดรายการสั่งซื้อได้ กรุณาลองใหม่
        </AlertV4>
      )}

      <div className="hv4-po-count">
        <span>
          <strong>{total}</strong> รายการ
        </span>
        <span className="hv4-po-sum">รวม {formatComma(sumAmount(rows))} บาท</span>
      </div>

      <div className="hv4-grid-table">
        <div className="hv4-po-table-inner" role="table" aria-label="รายการสั่งซื้อ">
          <div className="hv4-grid-row hv4-grid-row--head hv4-po-row" role="row">
            <span role="columnheader">เลขที่ใบแจ้งหนี้</span>
            <span role="columnheader">ลูกค้า</span>
            <span role="columnheader">วันที่สร้างรายการ</span>
            <span role="columnheader">ช่องทางชำระเงิน</span>
            <span role="columnheader">สถานะชำระเงิน</span>
            <span role="columnheader" className="hv4-po-num">
              ยอดเงิน (บาท)
            </span>
            <span role="columnheader">วันที่รับชำระ</span>
            <span role="columnheader">รายละเอียด</span>
          </div>
          {loading ? (
            <div className="hv4-grid-empty" role="status">
              กำลังโหลดรายการสั่งซื้อ
            </div>
          ) : rows.length === 0 ? (
            <div className="hv4-grid-empty">ไม่พบรายการตามตัวกรอง</div>
          ) : (
            rows.map((row) => (
              <div key={row.id} className="hv4-grid-row hv4-po-row" role="row">
                <span role="cell" className="hv4-po-no">
                  {row.code || "-"}
                </span>
                <span role="cell">{row.customer || "-"}</span>
                <span role="cell">{row.created || "-"}</span>
                <span role="cell" className="hv4-po-chips">
                  {row.channels.length
                    ? row.channels.map((channel) => (
                        <ChipV4 key={channel} tone="grey" size="sm" bold>
                          {channel}
                        </ChipV4>
                      ))
                    : "-"}
                </span>
                <span role="cell">{row.status || "-"}</span>
                <span role="cell" className="hv4-po-num">
                  {formatComma(row.amount)}
                </span>
                <span role="cell">{row.paidAt || "-"}</span>
                <span role="cell">
                  <ButtonV4 className="hv4-po-btn-soft" onClick={() => setOpenRow(row)}>
                    รายละเอียด
                  </ButtonV4>
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      <PaginationV4
        page={page}
        pageCount={pageCount}
        disabled={loading}
        onChange={(next) => handleLoad(applied, next)}
      />

      {openRow && (
        <ModalV4 title={`รายละเอียด ${openRow.code}`} width={760} onClose={() => setOpenRow(null)}>
          <div className="hv4-po-detail">
            <div>
              <b>เลขที่ใบแจ้งหนี้:</b> {openRow.code || "-"}
            </div>
            <div>
              <b>ลูกค้า:</b> {openRow.customer || "-"}
            </div>
            <div>
              <b>วันที่สร้างรายการ:</b> {openRow.created || "-"}
            </div>
            <div>
              <b>ช่องทางชำระเงิน:</b> {openRow.channels.join(", ") || "-"}
            </div>
            <div>
              <b>สถานะชำระเงิน:</b> {openRow.status || "-"}
            </div>
            <div>
              <b>ยอดเงิน:</b> {formatComma(openRow.amount)} บาท
            </div>

            <div className="hv4-po-items" role="table" aria-label="รายการสินค้า">
              <div className="hv4-po-item hv4-po-item--head" role="row">
                <span role="columnheader">รหัสสินค้า</span>
                <span role="columnheader">สินค้า</span>
                <span role="columnheader" className="hv4-po-num">
                  จำนวน
                </span>
                <span role="columnheader" className="hv4-po-num">
                  ราคา/หน่วย
                </span>
                <span role="columnheader" className="hv4-po-num">
                  รวม
                </span>
              </div>
              {openRow.items.length === 0 ? (
                <div className="hv4-po-items-empty">ไม่มีรายการสินค้าของใบแจ้งหนี้นี้</div>
              ) : (
                openRow.items.map((item) => (
                  <div key={item.id} className="hv4-po-item" role="row">
                    <span role="cell">{item.code || "-"}</span>
                    <span role="cell">{item.name || "-"}</span>
                    <span role="cell" className="hv4-po-num">
                      {formatComma(item.quantity, "en-US", false)}
                    </span>
                    <span role="cell" className="hv4-po-num">
                      {formatComma(item.unitPrice)}
                    </span>
                    <span role="cell" className="hv4-po-num">
                      {formatComma(item.total)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className="hv4-po-foot">
            <ButtonV4 className="hv4-po-btn-soft" onClick={() => setOpenRow(null)}>
              ปิด
            </ButtonV4>
          </div>
        </ModalV4>
      )}
    </div>
  );
};

export default CardPurchaseOrdersV4;
